'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { personalDetailsSchema, personalDetailsStep1Schema, personalDetailsStep2Schema, personalDetailsNameSchema, personalDetailsStatusSchema } from '@/lib/schema';
import { useWillStore } from '@/lib/store';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { RadioCard } from '@/app/components/ui/RadioCard';
import { StepWrapper } from '../StepWrapper';
import { FormNavigation } from '../FormNavigation';
import { AddressSection } from '../AddressSection';
import * as yup from 'yup';
import { User, Edit2, Check, X, ArrowRight, ShieldCheck, Heart, PiggyBank, Shield, Coins, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { ActionCard } from '@/app/components/ui/ActionCard';

type PersonalDetailsData = yup.InferType<typeof personalDetailsSchema>;

import { Checkbox } from '@/app/components/ui/Checkbox';
import { Button } from '@/app/components/ui/Button';
import { Modal } from '@/app/components/ui/Modal';
import { BooleanToggle } from '@/app/components/ui/BooleanToggle';

const addPartnerSchema = yup.object({
    fullName: yup.string().required('').test(
        'is-full-name',
        'Please enter at least a first and last name (e.g. "John Smith")',
        (value) => {
            if (!value || value.trim() === '') return true;
            return value.trim().indexOf(' ') !== -1;
        }
    ),
    dob: yup.string().required('Date of birth is required'),
    livesWithYou: yup.string().oneOf(['yes', 'no']).required('Please select an option'),
    partnerAddress: yup.object().when('livesWithYou', {
        is: 'no',
        then: (schema) => schema.shape({
            line1: yup.string().required('Address Line 1 is required'),
            line2: yup.string().optional(),
            city: yup.string().required('City is required'),
            postcode: yup.string().required('Postcode is required'),
        }),
        otherwise: (schema) => schema.notRequired(),
    }),
});

type AddPartnerFormData = yup.InferType<typeof addPartnerSchema>;

const AddPartnerForm = ({ onSave, onCancel }: { onSave: (data: any) => void, onCancel: () => void }) => {
    const { register, handleSubmit, watch, setValue, control, formState: { errors, isValid } } = useForm<AddPartnerFormData>({
        mode: 'onTouched',
        resolver: yupResolver(addPartnerSchema) as any,
        defaultValues: {
            livesWithYou: 'yes'
        }
    });

    const livesWithYou = watch('livesWithYou');

    return (
        <form onSubmit={handleSubmit(onSave)} className="space-y-6">
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-base font-medium text-gray-700">Partner's Full Name</Label>
                    <Input id="fullName" {...register('fullName')} />
                    {errors.fullName && <p className="text-destructive text-sm">{errors.fullName.message}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="dob" className="text-base font-medium text-gray-700">Date of Birth</Label>
                    <Input id="dob" type="tel" data-type="date-text" placeholder="dd/mm/yyyy" {...register('dob')} />
                    {errors.dob && <p className="text-destructive text-sm">{errors.dob.message}</p>}
                </div>

                <div className="space-y-6">
                    <Label className="text-base font-medium text-gray-700">Do they live at the same address as you?</Label>
                    <BooleanToggle
                        value={livesWithYou === 'yes'}
                        onChange={(val) => setValue('livesWithYou', val ? 'yes' : 'no', { shouldValidate: true })}
                        yesLabel="Yes"
                        noLabel="No"
                    />
                    <input type="hidden" {...register('livesWithYou')} />
                    {errors.livesWithYou && <p className="text-destructive text-sm">{errors.livesWithYou.message}</p>}
                </div>

                {livesWithYou === 'no' && (
                    <div className="space-y-2 animate-fade-in">
                        <AddressSection
                            prefix="partnerAddress"
                            label="Partner's Address"
                            labelClassName="font-bold text-teal"
                            register={register as any}
                            setValue={setValue as any}
                            errors={errors}
                            control={control as any}
                        />
                    </div>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
                <Button type="submit" disabled={!isValid} variant="primary">Save Partner</Button>
            </div>
        </form>
    );
};

export const PersonalDetails = () => {
    const {
        data,
        setData,
        setStep,
        currentStep,
        isEditingFromReview,
        setIsEditingFromReview,
        navigatingBack,
        clearNavigatingBack,
        mirrorEnabled,
        mirrorPartnerEmail,
        setMirrorEnabled,
        setMirrorPartnerEmail,
        markStepComplete,
    } = useWillStore();
    const normalizeBoolean = (v: unknown): boolean | undefined => (v === true || v === false ? v : undefined);

    // Helper to check if partner step is needed based on marital status
    const needsPartnerStep = (status: string | undefined) => {
        return ['married', 'civil_partnership', 'remarried', 'separated', 'engaged', 'cohabiting'].includes(status || '');
    };

    const [subStep, setSubStep] = useState<'details' | 'status' | 'mirror' | 'assets'>('details');

    // Track if this is a fresh navigation to this step (from menu or elsewhere)
    const prevStepRef = useRef<number | null>(null);

    useEffect(() => {
        // If navigating back from next section, go to last sub-step (assets)
        if (currentStep === 0 && navigatingBack) {
            setSubStep('assets');
            clearNavigatingBack();
        }
        // Always reset to 'details' when navigating TO this step from another step (forward navigation)
        // This ensures clicking "About You" from the menu always starts at the beginning
        else if (currentStep === 0 && prevStepRef.current !== null && prevStepRef.current !== 0 && !navigatingBack) {
            setSubStep('details');
        }
        // Also reset when editing from review
        else if (isEditingFromReview && currentStep === 0) {
            setSubStep('details');
        }

        prevStepRef.current = currentStep;
    }, [currentStep, isEditingFromReview, navigatingBack, clearNavigatingBack]);

    const subStepRef = useRef(subStep);
    subStepRef.current = subStep;

    // Cleanup helper - unused if we merge pages
    // const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        watch,
        trigger,
        control,
        getValues,
        reset,
    } = useForm<PersonalDetailsData>({
        resolver: (values, context, options) => {
            let schema;
            if (subStepRef.current === 'details') {
                schema = personalDetailsNameSchema;
            } else if (subStepRef.current === 'status') {
                schema = personalDetailsStatusSchema;
            } else {
                // Mirror and Assets use the step2 schema (survivorship)
                schema = personalDetailsStep2Schema;
            }
            return yupResolver(schema as any)(values, context, options as any) as any;
        },
        mode: 'onTouched',
        shouldUnregister: false,
        defaultValues: {
            fullName: data.personalDetails?.fullName || '',
            email: (data.personalDetails as any)?.email || '',
            otherNames: data.personalDetails?.otherNames || '',
            dateOfBirth: data.personalDetails?.dateOfBirth || '',
            maritalStatus: (data.personalDetails?.maritalStatus) || ('' as any),
            spouseFullName: (data.personalDetails as Partial<PersonalDetailsData>)?.spouseFullName || '',
            spouseLivesWithYou: normalizeBoolean((data.personalDetails as Partial<PersonalDetailsData>)?.spouseLivesWithYou),
            spouseAddress: (data.personalDetails as Partial<PersonalDetailsData>)?.spouseAddress,
            planningToMarry: normalizeBoolean((data.personalDetails as Partial<PersonalDetailsData>)?.planningToMarry),
            intendedPartnerFullName: (data.personalDetails as Partial<PersonalDetailsData>)?.intendedPartnerFullName || '',
            intendedPartnerLivesWithYou: normalizeBoolean(
                (data.personalDetails as Partial<PersonalDetailsData>)?.intendedPartnerLivesWithYou
            ),
            intendedPartnerAddress: (data.personalDetails as Partial<PersonalDetailsData>)?.intendedPartnerAddress,
            address: {
                line1: data.personalDetails?.address?.line1 || '',
                line2: data.personalDetails?.address?.line2 || '',
                city: data.personalDetails?.address?.city || '',
                postcode: data.personalDetails?.address?.postcode || '',
            },
            understandsSurvivorship: data.personalDetails?.understandsSurvivorship || false,
        },
    });

    // Force form reset when store data changes (e.g. loaded from DB)
    useEffect(() => {
        const pd = data.personalDetails;
        if (pd) {
            reset({
                fullName: pd.fullName || '',
                email: (pd as any)?.email || '',
                otherNames: pd.otherNames || '',
                dateOfBirth: pd.dateOfBirth || '',
                maritalStatus: (pd.maritalStatus) || ('' as any),
                spouseFullName: (pd as Partial<PersonalDetailsData>)?.spouseFullName || '',
                spouseLivesWithYou: normalizeBoolean((pd as Partial<PersonalDetailsData>)?.spouseLivesWithYou),
                spouseAddress: (pd as Partial<PersonalDetailsData>)?.spouseAddress,
                planningToMarry: normalizeBoolean((pd as Partial<PersonalDetailsData>)?.planningToMarry),
                intendedPartnerFullName: (pd as Partial<PersonalDetailsData>)?.intendedPartnerFullName || '',
                intendedPartnerLivesWithYou: normalizeBoolean(
                    (pd as Partial<PersonalDetailsData>)?.intendedPartnerLivesWithYou
                ),
                intendedPartnerAddress: (pd as Partial<PersonalDetailsData>)?.intendedPartnerAddress,
                address: {
                    line1: pd.address?.line1 || '',
                    line2: pd.address?.line2 || '',
                    city: pd.address?.city || '',
                    postcode: pd.address?.postcode || '',
                },
                understandsSurvivorship: pd.understandsSurvivorship || false,
            });
        }
    }, [data.personalDetails, reset]);

    useEffect(() => {
        register('spouseLivesWithYou');
        register('planningToMarry');
        register('intendedPartnerLivesWithYou');
    }, [register]);

    const maritalStatus = watch('maritalStatus');
    const spouseName = watch('spouseFullName');
    const intendedPartnerName = watch('intendedPartnerFullName');
    const mirrorEligible = ['married', 'civil_partnership', 'cohabiting', 'engaged', 'remarried', 'separated'].includes(maritalStatus || '');

    // Skip mirror sub-step if not eligible
    useEffect(() => {
        if (subStep === 'mirror' && !mirrorEligible) {
            setSubStep('assets');
        }
    }, [subStep, mirrorEligible]);
    const mirrorPartnerName = spouseName || intendedPartnerName || 'your partner';
    const mirrorPartnerFirstName = ((mirrorPartnerName || '').trim().split(' ')[0] || 'Partner');
    const previousMaritalStatusRef = useRef<string | undefined>(maritalStatus || undefined);

    const emptyAddress = { line1: '', line2: '', city: '', postcode: '' };

    const clearSpouseFields = React.useCallback(() => {
        setValue('spouseFullName', '');
        setValue('spouseLivesWithYou', undefined);
        setValue('spouseAddress', emptyAddress);
    }, [setValue]);

    const clearEngagementFields = React.useCallback(() => {
        setValue('planningToMarry', false, { shouldValidate: true });
        setValue('intendedPartnerFullName', '');
        setValue('intendedPartnerLivesWithYou', undefined);
        setValue('intendedPartnerAddress', emptyAddress);
    }, [setValue]);

    useEffect(() => {
        const previousStatus = previousMaritalStatusRef.current;

        if (previousStatus === maritalStatus) {
            return;
        }

        // Whenever the marital status changes, drop any answers tied to the previous selection
        if (previousStatus !== undefined) {
            clearSpouseFields();
            clearEngagementFields();
        }

        // Engagement-specific details should only persist while engaged
        if (maritalStatus !== 'engaged') {
            clearEngagementFields();
        } else {
            setValue('planningToMarry', true, { shouldValidate: true });
        }

        // Spouse/partner details should only persist for statuses that need them
        if (!['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(maritalStatus || '')) {
            clearSpouseFields();
        }

        previousMaritalStatusRef.current = maritalStatus;
    }, [maritalStatus, clearSpouseFields, clearEngagementFields, setValue]);

    const onSubmit = () => {
        if (subStep === 'details') {
            // Save partial data when moving to status step
            const formValues = getValues();
            setData({
                personalDetails: {
                    ...data.personalDetails,
                    fullName: formValues.fullName,
                    dateOfBirth: formValues.dateOfBirth,
                    address: formValues.address,
                    otherNames: formValues.otherNames,
                }
            });
            setSubStep('status');
            window.scrollTo(0, 0);
        } else if (subStep === 'status') {
            // Save partial data when moving to mirror or assets step
            const formValues = getValues();
            setData({
                personalDetails: {
                    ...data.personalDetails,
                    fullName: formValues.fullName,
                    dateOfBirth: formValues.dateOfBirth,
                    address: formValues.address,
                    otherNames: formValues.otherNames,
                    maritalStatus: formValues.maritalStatus,
                    spouseFullName: formValues.spouseFullName,
                    spouseLivesWithYou: normalizeBoolean(formValues.spouseLivesWithYou),
                    spouseAddress: formValues.spouseAddress,
                    planningToMarry: normalizeBoolean(formValues.planningToMarry),
                    intendedPartnerFullName: formValues.intendedPartnerFullName,
                    intendedPartnerLivesWithYou: normalizeBoolean(formValues.intendedPartnerLivesWithYou),
                    intendedPartnerAddress: formValues.intendedPartnerAddress,
                }
            });
            if (mirrorEligible) {
                setSubStep('mirror');
            } else {
                setSubStep('assets');
            }
            window.scrollTo(0, 0);
            return;
        } else if (subStep === 'mirror') {
            // Mirror step is optional; proceed to assets
            setSubStep('assets');
            window.scrollTo(0, 0);
            return;
        } else {
            const formValues = getValues();
            const maritalStatus = formValues.maritalStatus;
            const spouseSectionApplies = ['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(maritalStatus || '');
            const planningToMarry = normalizeBoolean(formValues.planningToMarry) === true;

            const finalData: PersonalDetailsData = {
                ...formValues,
                spouseLivesWithYou: normalizeBoolean(formValues.spouseLivesWithYou),
                planningToMarry: normalizeBoolean(formValues.planningToMarry),
                intendedPartnerLivesWithYou: normalizeBoolean(formValues.intendedPartnerLivesWithYou),
                spouseFullName: spouseSectionApplies ? formValues.spouseFullName : undefined,
                spouseAddress: spouseSectionApplies && formValues.spouseLivesWithYou === false ? formValues.spouseAddress : ({} as any),
                intendedPartnerFullName: planningToMarry ? formValues.intendedPartnerFullName : undefined,
                intendedPartnerAddress:
                    planningToMarry && formValues.intendedPartnerLivesWithYou === false ? formValues.intendedPartnerAddress : ({} as any),
                email: data.personalDetails?.email || formValues.email,
                understandsSurvivorship: true,
            };

            if (!(spouseSectionApplies && formValues.spouseLivesWithYou === false)) {
                delete (finalData as any).spouseAddress;
            }
            if (!(planningToMarry && formValues.intendedPartnerLivesWithYou === false)) {
                delete (finalData as any).intendedPartnerAddress;
            }

            setData({ personalDetails: finalData });
            markStepComplete(0); // Mark "About You" as complete

            if (isEditingFromReview) {
                setIsEditingFromReview(false);
                setStep(5); // Go back to Review
            } else {
                // Check if all sections are now complete - if so, go to Review
                // Use setTimeout to ensure state has updated before checking
                setTimeout(() => {
                    // Import is at top of file
                    const { isAllSectionsComplete } = require('@/lib/store');
                    if (isAllSectionsComplete()) {
                        setStep(5); // Go to Review
                    } else {
                        setStep(1); // Go to Guardians
                    }
                }, 0);
            }
        }
    };

    const getPartnerStepTitle = () => {
        // Unused now
        return "Your Partner";
    };

    const handleAssetsBack = () => {
        setSubStep('status');
    };

    const isStatusValid = () => {
        const status = watch('maritalStatus');
        if (!status) return false;

        const isMarried = ['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(status);
        const isEngaged = status === 'engaged';

        if (isMarried) {
            const name = watch('spouseFullName');
            const livesWith = watch('spouseLivesWithYou');
            const addr = watch('spouseAddress');

            const isNameValid = name && name.trim().includes(' ');
            const isLivesWithValid = livesWith !== undefined;
            const isAddressValid = livesWith === true || (addr?.line1 && addr?.city && addr?.postcode);

            return !!(isNameValid && isLivesWithValid && isAddressValid);
        }

        if (isEngaged) {
            const name = watch('intendedPartnerFullName');
            const livesWith = watch('intendedPartnerLivesWithYou');
            const addr = watch('intendedPartnerAddress');

            const isNameValid = name && name.trim().includes(' ');
            const isLivesWithValid = livesWith !== undefined;
            const isAddressValid = livesWith === true || (addr?.line1 && addr?.city && addr?.postcode);

            return !!(isNameValid && isLivesWithValid && isAddressValid);
        }

        return true;
    };

    return (
        <StepWrapper
            title={
                subStep === 'assets' ? "Assets"
                    : subStep === 'details' ? "Let's start with you"
                        : subStep === 'status' ? "Marital status?"
                            : undefined
            }
            description={
                subStep === 'details'
                    ? undefined
                    : subStep === 'assets'
                        ? (
                            <div className="space-y-6">
                                <p>A quick check about your assets.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2 text-left">
                                    <div className="space-y-3">
                                        <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                                <Check className="h-4 w-4 text-emerald-600" />
                                            </div>
                                            Assets covered by your Will:
                                        </h4>
                                        <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside ml-2">
                                            <li>Bank accounts and investments</li>
                                            <li>Property</li>
                                            <li>Other things you own, like your car, jewellery, clothes</li>
                                        </ul>
                                    </div>

                                    <div className="space-y-3">
                                        <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                                                <X className="h-4 w-4 text-red-600" />
                                            </div>
                                            Assets NOT covered by your Will:
                                        </h4>
                                        <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside ml-2">
                                            <li>Most pension plans</li>
                                            <li>Most life insurance policies</li>
                                            <li>Joint bank accounts</li>
                                            <li>Some types of jointly-owned property</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )
                        : undefined
            }
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {subStep === 'details' && (
                    <div className="space-y-8 animate-fade-in w-full md:w-1/2">
                        <div className="space-y-2">
                            <Label className="text-base font-medium text-foreground">Full Name</Label>
                            <div>
                                <Input id="fullName" {...register('fullName')} className="bg-white" />
                                {errors.fullName?.message && <p className="text-destructive text-sm mt-1">{errors.fullName.message}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="dateOfBirth" className="text-base font-medium text-foreground">Date of Birth</Label>
                            <Input
                                id="dateOfBirth"
                                type="tel"
                                data-type="date-text"
                                placeholder="dd/mm/yyyy"
                                {...register('dateOfBirth')}
                                className="bg-white"
                            />
                            {errors.dateOfBirth && <p className="text-destructive text-sm mt-1">{errors.dateOfBirth.message}</p>}
                        </div>

                        <div className="space-y-2">
                            <AddressSection
                                prefix="address"
                                label="Address"
                                labelClassName="text-base font-medium text-foreground"
                                register={register}
                                setValue={setValue}
                                errors={errors}
                                control={control}
                            />
                        </div>

                        <FormNavigation
                            isFirstStep
                            isValid={!!watch('fullName') && !!watch('dateOfBirth') && !!watch('address.line1') && !!watch('address.city') && !!watch('address.postcode') && (watch('fullName') || '').trim().indexOf(' ') !== -1}
                        />
                    </div>
                )}

                {subStep === 'status' && (
                    <div className="space-y-10 animate-fade-in">
                        <div className="w-full md:w-1/2 space-y-4">
                            {watch('maritalStatus') && (
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setValue('maritalStatus', '' as any)}
                                        className="text-sm text-primary font-medium flex items-center hover:underline animate-fade-in"
                                    >
                                        <Edit2 className="w-3 h-3 mr-1" />
                                        Change
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-3">
                                {[
                                    { value: 'single', label: 'Single' },
                                    { value: 'married', label: 'Married' },
                                    { value: 'civil_partnership', label: 'Civil Partnership' },
                                    { value: 'cohabiting', label: 'Cohabiting/Long-term Partner' },
                                    { value: 'engaged', label: 'Engaged' },
                                    { value: 'widowed', label: 'Widowed' },
                                    { value: 'remarried', label: 'Widowed and remarried' },
                                    { value: 'separated', label: 'Married/Civil Partnership but separated' },
                                    { value: 'divorced', label: 'Divorced' },
                                ].filter(option => !watch('maritalStatus') || watch('maritalStatus') === option.value).map((option, index) => {
                                    const isSelected = watch('maritalStatus') === option.value;
                                    const showingAll = !watch('maritalStatus');

                                    return (
                                        <div
                                            key={option.value}
                                            className={`transform transition-all duration-300 ease-out ${showingAll
                                                ? 'opacity-100 translate-y-0'
                                                : isSelected
                                                    ? 'opacity-100 scale-100'
                                                    : 'opacity-0 scale-95 h-0 overflow-hidden'
                                                }`}
                                            style={{
                                                animationDelay: showingAll ? `${index * 50}ms` : '0ms',
                                                animation: showingAll ? `fadeSlideIn 0.4s ease-out ${index * 50}ms both` : undefined
                                            }}
                                        >
                                            <RadioCard
                                                title={option.label}
                                                checked={isSelected}
                                                className="transition-all duration-200"
                                                onClick={() => {
                                                    if (!watch('maritalStatus')) {
                                                        setValue('maritalStatus', option.value as any, { shouldValidate: true });

                                                        if (option.value === 'engaged') {
                                                            setValue('planningToMarry', true, { shouldValidate: true });
                                                        }

                                                        if (!needsPartnerStep(option.value)) {
                                                            setValue('spouseFullName', '');
                                                            setValue('spouseLivesWithYou', undefined);
                                                            setValue('intendedPartnerFullName', '');
                                                            setValue('intendedPartnerLivesWithYou', undefined);
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {errors.maritalStatus && <p className="text-destructive text-base">{errors.maritalStatus.message}</p>}
                        </div>

                        {(watch('maritalStatus') === 'separated') && (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-base text-amber-800 animate-fade-in flex gap-3 items-start">
                                <div className="mt-1 w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                                    <span className="font-bold text-xs text-amber-800">!</span>
                                </div>
                                <div>
                                    <span className="font-semibold block mb-1">Important Note:</span>
                                    Divorce/Dissolution affects gifts/appointments to an ex-spouse/partner as if they'd died (unless the will says otherwise), but separation does not.
                                </div>
                            </div>
                        )}

                        {/* Spouse/Partner Details Form - Inline */}
                        {['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(watch('maritalStatus') || '') && (
                            <div className="space-y-8 pt-6 border-t border-gray-100 animate-fade-in">
                                <div className="w-full md:w-1/2 space-y-2">
                                    <Label className="text-base font-medium text-foreground">
                                        {watch('maritalStatus') === 'separated' ? "Your Estranged Spouse/Civil Partner" :
                                            watch('maritalStatus') === 'civil_partnership' ? "Your Civil Partner" :
                                                watch('maritalStatus') === 'cohabiting' ? "Your Partner" : "Your Spouse"} - Full Name
                                    </Label>

                                    <div className="space-y-2">
                                        <div>
                                            <Input
                                                id="spouseFullName"
                                                {...register('spouseFullName')}
                                                className="bg-white"
                                            />
                                            {errors.spouseFullName && <p className="text-destructive text-sm mt-1">{errors.spouseFullName.message}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full md:w-1/2 space-y-4">
                                    <Label className="text-base font-medium text-foreground">Do they live at the same address as you?</Label>
                                    <BooleanToggle
                                        value={watch('spouseLivesWithYou')}
                                        onChange={(val) => setValue('spouseLivesWithYou', val, { shouldValidate: true })}
                                    />
                                    {errors.spouseLivesWithYou && <p className="text-destructive text-sm">{errors.spouseLivesWithYou.message}</p>}
                                </div>

                                {watch('spouseLivesWithYou') === false && (
                                    <div className="w-full md:w-1/2 animate-fade-in">
                                        <AddressSection
                                            prefix="spouseAddress"
                                            label="Partner's Address"
                                            labelClassName="text-base font-medium text-foreground"
                                            register={register}
                                            setValue={setValue}
                                            errors={errors}
                                            control={control}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {watch('maritalStatus') === 'engaged' && (
                            <div className="space-y-8 pt-6 border-t border-gray-100 animate-fade-in">
                                <div className="w-full md:w-1/2 space-y-2">
                                    <Label className="text-base font-medium text-foreground">Your Fiancé(e) - Full Name</Label>
                                    <div className="space-y-2">
                                        <div>
                                            <Input
                                                id="intendedPartnerFullName"
                                                {...register('intendedPartnerFullName')}
                                                className="bg-white"
                                            />
                                            {errors.intendedPartnerFullName && <p className="text-destructive text-sm mt-1">{errors.intendedPartnerFullName.message}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full md:w-1/2 space-y-4">
                                    <Label className="text-base font-medium text-foreground">Do they live at the same address as you?</Label>
                                    <BooleanToggle
                                        value={watch('intendedPartnerLivesWithYou')}
                                        onChange={(val) => setValue('intendedPartnerLivesWithYou', val, { shouldValidate: true })}
                                    />
                                    {errors.intendedPartnerLivesWithYou && <p className="text-destructive text-sm">{errors.intendedPartnerLivesWithYou.message}</p>}
                                </div>

                                {watch('intendedPartnerLivesWithYou') === false && (
                                    <div className="w-full md:w-1/2 animate-fade-in">
                                        <AddressSection
                                            prefix="intendedPartnerAddress"
                                            label="Fiancé(e)'s Address"
                                            labelClassName="text-base font-medium text-foreground"
                                            register={register}
                                            setValue={setValue}
                                            errors={errors}
                                            control={control}
                                        />
                                    </div>
                                )}

                                <div className="pt-2 w-full md:w-1/2">
                                    <button
                                        type="button"
                                        onClick={() => setValue('planningToMarry', !watch('planningToMarry'), { shouldValidate: true })}
                                        className={`w-full flex items-center gap-4 p-5 border rounded-xl transition-all duration-300 text-left ${watch('planningToMarry') === true
                                            ? 'border-primary bg-primary/5 scale-[1.01] shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-primary hover:bg-primary/5 hover:scale-[1.01]'
                                            }`}
                                    >
                                        <div className={`h-6 w-6 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${watch('planningToMarry') === true
                                            ? 'border-primary bg-primary text-white'
                                            : 'border-gray-300 bg-white'
                                            }`}>
                                            {watch('planningToMarry') === true && <Check className="h-4 w-4" />}
                                        </div>
                                        <span className="font-medium text-foreground">
                                            I want my Will to remain valid after I marry {watch('intendedPartnerFullName') || '[Partner Name]'}.
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}

                        <FormNavigation
                            onBack={() => setSubStep('details')}
                            isValid={isStatusValid()}
                            onNext={onSubmit}
                        />
                    </div>
                )}

                {subStep === 'mirror' && mirrorEligible && (
                    <div className="space-y-10">
                            <div className="space-y-4 pt-2 animate-fade-in" style={{ animationDelay: '0ms' }}>
                                <div className="space-y-3">
                                    <div className="pb-3 border-b border-gray-200">
                                        <h2 className="section-title">
                                            {mirrorPartnerFirstName}'s Will
                                        </h2>
                                    </div>
                                    <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                                        <ul className="list-disc list-outside ml-5 space-y-2">
                                            <li>A Mirror Will creates separate legal documents for you and your partner</li>
                                            <li>They usually reflect each other's wishes (leaving everything to each other)</li>
                                            <li>You are free to make them different if you choose</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <motion.div 
                                className="grid grid-cols-1 md:grid-cols-3 gap-4"
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                                    }
                                }}
                            >
                                <motion.div 
                                    className="p-6 bg-white border border-gray-200 rounded-xl flex flex-col gap-4 shadow-sm group items-center text-center relative overflow-hidden"
                                    variants={{
                                        hidden: { y: 20, opacity: 0, scale: 0.95 },
                                        visible: { 
                                            y: 0, 
                                            opacity: 1, 
                                            scale: 1, 
                                            transition: { type: "spring", stiffness: 100, damping: 15 } 
                                        }
                                    }}
                                    whileHover={{ 
                                        y: -8, 
                                        boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                                        borderColor: "#358C7F" // teal
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-teal/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <motion.div 
                                        className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center shrink-0 border border-teal/20 shadow-sm text-teal z-10"
                                        variants={{
                                            hidden: { scale: 0, rotate: -90 },
                                            visible: { 
                                                scale: 1, 
                                                rotate: 0, 
                                                transition: { type: "spring", stiffness: 200, damping: 12, delay: 0.2 } 
                                            }
                                        }}
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <Shield className="h-7 w-7 fill-current" />
                                    </motion.div>
                                    <div className="space-y-1 z-10">
                                        <h4 className="font-bold text-foreground text-base font-body">Control (not intestacy)</h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed px-1">
                                            Mirror wills ensure the survivor gets what you intend, and you decide how assets pass to children rather than relying on statutory rules.
                                        </p>
                                    </div>
                                </motion.div>
                                
                                <motion.div 
                                    className="p-6 bg-white border border-gray-200 rounded-xl flex flex-col gap-4 shadow-sm group items-center text-center relative overflow-hidden"
                                    variants={{
                                        hidden: { y: 20, opacity: 0, scale: 0.95 },
                                        visible: { 
                                            y: 0, 
                                            opacity: 1, 
                                            scale: 1, 
                                            transition: { type: "spring", stiffness: 100, damping: 15 } 
                                        }
                                    }}
                                    whileHover={{ 
                                        y: -8, 
                                        boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                                        borderColor: "#358C7F" // teal
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-teal/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <motion.div 
                                        className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center shrink-0 border border-teal/20 shadow-sm text-teal z-10"
                                        variants={{
                                            hidden: { scale: 0, rotate: -90 },
                                            visible: { 
                                                scale: 1, 
                                                rotate: 0, 
                                                transition: { type: "spring", stiffness: 200, damping: 12, delay: 0.3 } 
                                            }
                                        }}
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <Users className="h-7 w-7" />
                                    </motion.div>
                                    <div className="space-y-1 z-10">
                                        <h4 className="font-bold text-foreground text-base font-body">Appoint the right people</h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed px-1">
                                            You both name the same executors (and guardians if relevant) so the people you trust handle the estate and children’s care.
                                        </p>
                                    </div>
                                </motion.div>

                                <motion.div 
                                    className="p-6 bg-white border border-gray-200 rounded-xl flex flex-col gap-4 shadow-sm group items-center text-center relative overflow-hidden"
                                    variants={{
                                        hidden: { y: 20, opacity: 0, scale: 0.95 },
                                        visible: { 
                                            y: 0, 
                                            opacity: 1, 
                                            scale: 1, 
                                            transition: { type: "spring", stiffness: 100, damping: 15 } 
                                        }
                                    }}
                                    whileHover={{ 
                                        y: -8, 
                                        boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                                        borderColor: "#358C7F" // teal
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-teal/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <motion.div 
                                        className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center shrink-0 border border-teal/20 shadow-sm text-teal z-10"
                                        variants={{
                                            hidden: { scale: 0, rotate: -90 },
                                            visible: { 
                                                scale: 1, 
                                                rotate: 0, 
                                                transition: { type: "spring", stiffness: 200, damping: 12, delay: 0.4 } 
                                            }
                                        }}
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <Coins className="h-7 w-7" />
                                    </motion.div>
                                    <div className="space-y-1 z-10">
                                        <h4 className="font-bold text-foreground text-base font-body">Keep tax planning aligned</h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed px-1">
                                            Mirror wills help structure gifts to use the spouse exemption and preserve transferable nil-rate bands.
                                        </p>
                                    </div>
                                </motion.div>
                            </motion.div>

                            <div className="w-full md:w-1/2 space-y-2 animate-fade-in" style={{ animationDelay: '400ms', animationFillMode: 'both' }}>
                                <p className="text-base font-semibold text-foreground">
                                    Would you like to create a Will for {mirrorPartnerFirstName}?
                                </p>
                                <BooleanToggle
                                    value={mirrorEnabled as any}
                                    onChange={(val) => setMirrorEnabled(val)}
                                />
                            </div>

                            {mirrorEnabled === true && (
                                <div className="space-y-2 w-full md:w-1/2 animate-fade-in">
                                    <Label className="text-base font-medium text-foreground">{mirrorPartnerFirstName}'s email</Label>
                                    <Input
                                        value={mirrorPartnerEmail || ''}
                                        onChange={(e) => setMirrorPartnerEmail(e.target.value)}
                                        placeholder="partner@email.com"
                                        className="bg-white"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        We’ll use this to send their document link. You can update it before payment.
                                    </p>
                                </div>
                            )}

                        <div className="animate-fade-in" style={{ animationDelay: '800ms', animationFillMode: 'both' }}>
                            <FormNavigation
                                onBack={() => setSubStep('status')}
                                isValid={mirrorEnabled !== null && mirrorEnabled !== undefined}
                                onNext={onSubmit}
                            />
                        </div>
                    </div>
                )}

                {subStep === 'assets' && (
                    <div className="space-y-10 animate-fade-in">
                        <FormNavigation
                            onBack={() => mirrorEligible ? setSubStep('mirror') : setSubStep('status')}
                            isValid={true}
                            onNext={onSubmit}
                        />
                    </div>
                )}
            </form>
        </StepWrapper>
    );
};
